import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import axios from "axios";
import { API, fileUrl } from "@/lib/api";
import { STATIC_EXPORT } from "@/lib/config";
import bakedLeadership from "@/lib/bakedLeadership.json";

export default function Leadership() {
  const [directors, setDirectors] = useState(STATIC_EXPORT ? bakedLeadership : null);

  useEffect(() => {
    if (STATIC_EXPORT) return;
    axios
      .get(`${API}/api/leadership`)
      .then((r) => setDirectors(r.data))
      .catch(() => setDirectors([]));
  }, []);

  // When profiles load in, the page grows - nudge the stacking sections to re-measure.
  useEffect(() => {
    if (!directors || directors.length === 0) return;
    const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 120);
    return () => clearTimeout(t);
  }, [directors]);

  // Hidden until the team publishes real director profiles in the Studio.
  if (!directors || directors.length === 0) return null;

  return (
    <section id="leadership" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-white" data-testid="leadership-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            Leadership
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#5B4199] leading-[1.04]">
            The people behind Expante.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {directors.map((d, i) => (
            <motion.article
              key={d.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="card-jeton rounded-[28px] bg-[#F6F3FE] shadow-[0_24px_56px_-18px_rgba(76,29,149,0.18)] overflow-hidden"
              data-testid={`director-card-${d.id}`}
            >
              {fileUrl(d.photo) ? (
                <div className="h-72 overflow-hidden">
                  <img src={fileUrl(d.photo)} alt={d.name} className="w-full h-full object-cover object-[center_20%]" loading="lazy" />
                </div>
              ) : (
                <div className="h-72 bg-[#5B4199] flex items-center justify-center">
                  <span className="font-display text-7xl font-extrabold text-white/25">
                    {d.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                </div>
              )}
              <div className="p-7">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="font-display text-xl font-extrabold text-[#0B132B]">{d.name}</h3>
                    {d.title && <p className="text-sm font-semibold text-[#5B4199] mt-0.5">{d.title}</p>}
                  </div>
                  {d.linkedin_url && (
                    <a
                      href={d.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${d.name} on LinkedIn`}
                      data-testid={`director-linkedin-${d.id}`}
                      className="shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-[#0B132B] text-white hover:bg-[#5B4199] transition-colors duration-300"
                    >
                      <Linkedin className="h-4.5 w-4.5" />
                    </a>
                  )}
                </div>
                {d.bio && <p className="text-sm text-[#475569] leading-relaxed">{d.bio}</p>}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
